import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { GridSettingsMapperService } from './grid-settings-mapper.service';
import { MessageService } from 'primeng/api';

interface GridColumn {
  fieldKeyName: string;
  fieldName: string;
  displayOrder?: number;
}

@Component({
    selector: 'app-grid-settings-mapper',
    templateUrl: './grid-settings-mapper.component.html',
    styleUrls: ['./grid-settings-mapper.component.scss'],
    standalone: false
})
export class GridSettingsMapperComponent implements OnInit {
  // v13-migration placeholder (Ivy basic template-check requires the symbol to exist; undefined at runtime == v12 behaviour; Phase 2: align template)
  errorMsg: any;
  @Input() availableColumns: GridColumn[] = [];
  @Input() customCols: GridColumn[] = [];
  @Input() defaultColumns: GridColumn[] = [];
  @Input() category: string = '';
  @Output() onSaved = new EventEmitter<{ selected: GridColumn[] }>();

  sourceColumns: GridColumn[] = [];
  targetColumns: GridColumn[] = [];
  selectedSource: GridColumn[] = [];
  selectedTarget: GridColumn[] = [];
  sourceFilter = '';
  targetFilter = '';
  saving = false;

  constructor(private readonly messageService: MessageService,private readonly gridSettingsService: GridSettingsMapperService) {}

  ngOnInit(): void {
    // Remove columns already in customCols from availableColumns
    const customKeys = new Set(this.customCols.map(col => col.fieldKeyName));
    this.sourceColumns = this.availableColumns.filter(col => !customKeys.has(col.fieldKeyName));
    // Always include default columns at the start of targetColumns
    this.targetColumns = [...this.customCols.filter(col => !this.defaultColumns.some(def => def.fieldKeyName === col.fieldKeyName))];
  }

  getFilteredSource(): GridColumn[] {
    if (!this.sourceFilter) return this.sourceColumns;
    return this.sourceColumns.filter(col =>
      col.fieldName.toLowerCase().includes(this.sourceFilter.toLowerCase())
    );
  }

  getFilteredTarget(): GridColumn[] {
    if (!this.targetFilter) return this.targetColumns;
    return this.targetColumns.filter(col =>
      col.fieldName.toLowerCase().includes(this.targetFilter.toLowerCase())
    );
  }

  isSourceSelected(col: GridColumn): boolean {
    return this.selectedSource.some(c => c.fieldKeyName === col.fieldKeyName);
  }

  isTargetSelected(col: GridColumn): boolean {
    return this.selectedTarget.some(c => c.fieldKeyName === col.fieldKeyName);
  }

  toggleSourceSelection(col: GridColumn) {
    const idx = this.selectedSource.findIndex(c => c.fieldKeyName === col.fieldKeyName);
    if (idx > -1) this.selectedSource.splice(idx, 1);
    else this.selectedSource.push(col);
  }

  toggleTargetSelection(col: GridColumn) {
    const idx = this.selectedTarget.findIndex(c => c.fieldKeyName === col.fieldKeyName);
    if (idx > -1) this.selectedTarget.splice(idx, 1);
    else this.selectedTarget.push(col);
  }

  moveToTarget() {
    // Prevent moving default columns
    this.targetColumns = [...this.targetColumns, ...this.selectedSource.filter(col => !this.defaultColumns.some(def => def.fieldKeyName === col.fieldKeyName))];
    this.sourceColumns = this.sourceColumns.filter(col =>
      !this.selectedSource.some(sel => sel.fieldKeyName === col.fieldKeyName)
    );
    this.selectedSource = [];
  }

  moveAllToTarget() {
    // Prevent moving default columns
    this.targetColumns = [...this.targetColumns, ...this.sourceColumns.filter(col => !this.defaultColumns.some(def => def.fieldKeyName === col.fieldKeyName))];
    this.sourceColumns = [];
    this.selectedSource = [];
  }

  moveToSource() {
    // Prevent removing default columns
    this.sourceColumns = [...this.sourceColumns, ...this.selectedTarget.filter(col => !this.defaultColumns.some(def => def.fieldKeyName === col.fieldKeyName))];
    this.targetColumns = this.targetColumns.filter(col =>
      !this.selectedTarget.some(sel => sel.fieldKeyName === col.fieldKeyName) || this.defaultColumns.some(def => def.fieldKeyName === col.fieldKeyName)
    );
    this.selectedTarget = [];
  }

  moveAllToSource() {
    // Prevent removing default columns
    this.sourceColumns = [...this.sourceColumns, ...this.targetColumns.filter(col => !this.defaultColumns.some(def => def.fieldKeyName === col.fieldKeyName))];
    this.targetColumns = [];
    this.selectedTarget = [];
  }

  moveUp() {
    // Prevent moving default columns
    if (this.selectedTarget.length === 0) return;
    // Sort selected indices ascending
    const indices = this.selectedTarget
      .map(sel => this.targetColumns.findIndex(c => c.fieldKeyName === sel.fieldKeyName))
      .sort((a, b) => a - b);

    // If the first selected item is already at the top, we can't move up
    if (indices[0] === 0) return;

    indices.forEach(idx => {
      const temp = this.targetColumns[idx];
      this.targetColumns[idx] = this.targetColumns[idx - 1];
      this.targetColumns[idx - 1] = temp;
    });
  }

  moveDown() {
    // Prevent moving default columns
    if (this.selectedTarget.length === 0) return;
    // Sort selected indices descending
    const indices = this.selectedTarget
      .map(sel => this.targetColumns.findIndex(c => c.fieldKeyName === sel.fieldKeyName))
      .sort((a, b) => b - a); // Sort descending to move from bottom up

    if (indices[0] === this.targetColumns.length - 1) return;

    indices.forEach(idx => {
      const temp = this.targetColumns[idx];
      this.targetColumns[idx] = this.targetColumns[idx + 1];
      this.targetColumns[idx + 1] = temp;
    });
  }

  onSave() {
    this.saving = true;
    this.messageService.clear();
    // Set displayOrder based on array order, always include default columns at the start
    const columnsToSave = this.targetColumns.map((col, idx) => ({
      fieldKeyName: col.fieldKeyName,
      displayOrder: idx + 1
    }));
    this.gridSettingsService.saveTableSettings(this.category,{ userViewGridFields:columnsToSave}).subscribe(res=>{
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Settings saved successfully' });
        this.saving = false;
        this.onSaved.emit();
    },error => {
        this.saving = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: error?.error || 'Failed to save settings' });
      });
  }
}